import { Controller } from "@hotwired/stimulus"

// Wraps a section that contains an attachment-viewer dialog and any
// number of trigger tiles. Each trigger fires `open` with an
// `index` param; the controller pre-rendered every slide server-side
// and just toggles which one is visible.
export default class extends Controller {
  static targets = [
    "dialog", "slide", "thumb", "thumbStrip",
    "prevButton", "nextButton", "caption", "position"
  ]

  declare readonly dialogTarget: HTMLDialogElement
  declare readonly hasDialogTarget: boolean
  declare readonly slideTargets: HTMLElement[]
  declare readonly thumbTargets: HTMLButtonElement[]
  declare readonly thumbStripTarget: HTMLElement
  declare readonly prevButtonTarget: HTMLButtonElement
  declare readonly nextButtonTarget: HTMLButtonElement
  declare readonly captionTarget: HTMLElement
  declare readonly positionTarget: HTMLElement

  private currentIndex = 0

  open(event: Event & { params: { index: number } }): void {
    event.preventDefault()
    if (!this.hasDialogTarget) return
    this.currentIndex = event.params.index ?? 0
    this.applyIndex()
    this.dialogTarget.showModal()
  }

  close(): void {
    if (this.hasDialogTarget) this.dialogTarget.close()
  }

  prev(): void {
    if (this.currentIndex <= 0) return
    this.currentIndex -= 1
    this.applyIndex()
  }

  next(): void {
    if (this.currentIndex >= this.slideTargets.length - 1) return
    this.currentIndex += 1
    this.applyIndex()
  }

  selectIndex(event: Event & { params: { index: number } }): void {
    this.currentIndex = event.params.index
    this.applyIndex()
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.prev()
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    }
  }

  // Native <dialog> close fires a `close` event — pause any playing
  // video so audio doesn't keep going behind the scenes.
  onDialogClose(): void {
    this.slideTargets.forEach(slide => {
      const video = slide.querySelector("video")
      if (video) video.pause()
    })
  }

  private applyIndex(): void {
    this.slideTargets.forEach((slide, idx) => {
      const active = idx === this.currentIndex
      slide.classList.toggle("hidden", !active)
      const video = slide.querySelector("video")
      if (video && !active) video.pause()
    })
    this.thumbTargets.forEach((thumb, idx) => {
      const active = idx === this.currentIndex
      thumb.classList.toggle("border-accent", active)
      thumb.classList.toggle("border-transparent", !active)
      if (active) thumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
    })
    this.prevButtonTarget.toggleAttribute("disabled", this.currentIndex === 0)
    this.nextButtonTarget.toggleAttribute("disabled", this.currentIndex === this.slideTargets.length - 1)
    this.updateCaption()
  }

  private updateCaption(): void {
    const slide = this.slideTargets[this.currentIndex]
    if (!slide) return
    const caption = slide.dataset.caption || ""
    this.captionTarget.textContent = caption
    this.positionTarget.textContent = `${this.currentIndex + 1} / ${this.slideTargets.length}`
  }
}
